/**
 * Browser-side IndexedDB store for SIGS payloads and metadata.
 * Mirrors the filesystem operations in vite-middleware/sigsStorage.ts.
 */
import type { LocalSigsEntry, MetadataCacheEntry } from '../types/sigs';

const DB_NAME = 'ai-studio-sigs';
const DB_VERSION = 3;
const STORE_PAYLOADS = 'payloads';
const STORE_METADATA = 'metadata_v2';
const LEGACY_STORE_METADATA = 'metadata';
const STORE_TIMELINE = 'timeline_cache';

interface PayloadRecord {
  cv: string;
  payload: unknown;
  /** Last-access or creation timestamp (ms). Updated by touchPayload. */
  timestamp: number;
  startTime?: string;
  promptPreview?: string;
  signalCv?: string;
}

function openDb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      let metadataStore: IDBObjectStore;
      if (!db.objectStoreNames.contains(STORE_PAYLOADS)) {
        db.createObjectStore(STORE_PAYLOADS, { keyPath: 'cv' });
      }
      if (!db.objectStoreNames.contains(STORE_METADATA)) {
        metadataStore = db.createObjectStore(STORE_METADATA, {
          keyPath: 'signalId',
        });
      } else {
        metadataStore = req.transaction!.objectStore(STORE_METADATA);
      }
      if (
        db.objectStoreNames.contains(LEGACY_STORE_METADATA) &&
        req.transaction
      ) {
        const legacyRequest = req.transaction
          .objectStore(LEGACY_STORE_METADATA)
          .getAll();
        legacyRequest.onsuccess = () => {
          for (const entry of legacyRequest.result as MetadataCacheEntry[]) {
            if (entry.signalId) metadataStore.put(entry);
          }
        };
      }
      if (!db.objectStoreNames.contains(STORE_TIMELINE)) {
        db.createObjectStore(STORE_TIMELINE, { keyPath: 'key' });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('Database error'));
  });
}

function extractPromptPreview(payload: unknown): string | undefined {
  try {
    const root = payload as Record<string, unknown>;
    const signals = Array.isArray(root.value)
      ? (root.value as unknown[])
      : [root];
    for (const sig of signals) {
      const signal = sig as Record<string, unknown>;
      const compressed = (
        signal.CustomProperties as Record<string, unknown> | undefined
      )?.CompressedData as Record<string, unknown> | undefined;
      if (!compressed) continue;
      const events = compressed.ExecutionEvents as
        | Record<string, unknown>[]
        | undefined;
      if (!Array.isArray(events)) continue;
      const chatEvent = events.find(
        (e) => e.EventType === 'CopilotChatExecutionEvent',
      );
      const prompt = chatEvent?.UserPrompt;
      if (typeof prompt === 'string' && prompt.trim()) {
        return prompt.slice(0, 80).replace(/\s+/g, ' ').trim();
      }
    }
  } catch {
    // ignore
  }
  return undefined;
}

export async function dbSavePayload(
  cv: string,
  payload: unknown,
): Promise<void> {
  const db = await openDb();
  const p = payload as Record<string, unknown>;
  let startTime: string | undefined;
  let signalCv: string | undefined;
  if (typeof p.StartTime === 'string') startTime = p.StartTime;
  if (typeof p.CV === 'string') {
    signalCv = p.CV;
  } else if (Array.isArray(p.value)) {
    const first = (p.value as Record<string, unknown>[])[0];
    if (first && typeof first.CV === 'string') signalCv = first.CV;
  }
  const record: PayloadRecord = {
    cv,
    payload,
    timestamp: Date.now(),
    startTime,
    promptPreview: extractPromptPreview(payload),
    signalCv,
  };
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PAYLOADS, 'readwrite');
    const req = tx.objectStore(STORE_PAYLOADS).put(record);
    req.onsuccess = () => resolve();
    req.onerror = () => reject(req.error ?? new Error('Database error'));
  });
}

export async function dbGetPayload(cv: string): Promise<unknown> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PAYLOADS, 'readonly');
    const store = tx.objectStore(STORE_PAYLOADS);
    const req = store.get(cv);
    req.onsuccess = () => {
      if (req.result) {
        resolve((req.result as PayloadRecord).payload);
        return;
      }
      // Fall back to prefix match (handles .1 CV extensions)
      const allReq = store.getAll();
      allReq.onsuccess = () => {
        const records = allReq.result as PayloadRecord[];
        const prefix = cv + '.';
        const match = records.find(
          (r) => r.cv.startsWith(prefix) || cv.startsWith(r.cv + '.'),
        );
        resolve(match ? match.payload : null);
      };
      allReq.onerror = () =>
        reject(allReq.error ?? new Error('Database error'));
    };
    req.onerror = () => reject(req.error ?? new Error('Database error'));
  });
}

export async function dbTouchPayload(cv: string): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PAYLOADS, 'readwrite');
    const store = tx.objectStore(STORE_PAYLOADS);
    const req = store.get(cv);
    req.onsuccess = () => {
      if (req.result) {
        const record = req.result as PayloadRecord;
        record.timestamp = Date.now();
        store.put(record);
      }
      resolve();
    };
    req.onerror = () => reject(req.error ?? new Error('Database error'));
  });
}

export async function dbListPayloads(): Promise<LocalSigsEntry[]> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_PAYLOADS, 'readonly');
    const req = tx.objectStore(STORE_PAYLOADS).getAll();
    req.onsuccess = () => {
      const records = req.result as PayloadRecord[];
      resolve(
        records.map((r) => ({
          cv: r.cv,
          filename: r.cv,
          timestamp: new Date(r.timestamp).toISOString(),
          startTime: r.startTime,
          promptPreview: r.promptPreview,
          signalCv: r.signalCv,
        })),
      );
    };
    req.onerror = () => reject(req.error ?? new Error('Database error'));
  });
}

export async function dbSaveMetadataEntries(
  entries: MetadataCacheEntry[],
): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_METADATA, 'readwrite');
    const store = tx.objectStore(STORE_METADATA);
    for (const entry of entries) {
      store.put(entry);
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Database error'));
  });
}

export async function dbGetMetadataCache(): Promise<
  Record<string, MetadataCacheEntry>
> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_METADATA, 'readonly');
    const req = tx.objectStore(STORE_METADATA).getAll();
    req.onsuccess = () => {
      const entries = req.result as MetadataCacheEntry[];
      const cache: Record<string, MetadataCacheEntry> = {};
      for (const e of entries) cache[e.signalId] = e;
      resolve(cache);
    };
    req.onerror = () => reject(req.error ?? new Error('Database error'));
  });
}

export async function dbClearAll(): Promise<void> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const stores = [STORE_PAYLOADS, STORE_METADATA];
    if (db.objectStoreNames.contains(LEGACY_STORE_METADATA)) {
      stores.push(LEGACY_STORE_METADATA);
    }
    const tx = db.transaction(stores, 'readwrite');
    tx.objectStore(STORE_PAYLOADS).clear();
    tx.objectStore(STORE_METADATA).clear();
    if (db.objectStoreNames.contains(LEGACY_STORE_METADATA)) {
      tx.objectStore(LEGACY_STORE_METADATA).clear();
    }
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error ?? new Error('Database error'));
  });
}

export async function dbGetMetadataEntry(
  identifier: string,
): Promise<MetadataCacheEntry | null> {
  const db = await openDb();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_METADATA, 'readonly');
    const store = tx.objectStore(STORE_METADATA);
    const req = store.get(identifier);
    req.onsuccess = () => {
      if (req.result) {
        resolve(req.result as MetadataCacheEntry);
        return;
      }
      const allReq = store.getAll();
      allReq.onsuccess = () => {
        const entries = (allReq.result as MetadataCacheEntry[]) || [];
        const prefix = identifier + '.';
        const match =
          entries.find((e) => e.cv === identifier) ??
          entries.find((e) => e.itemId === identifier) ??
          entries.find((e) => e.itemId.startsWith(prefix));
        resolve(match ?? null);
      };
      allReq.onerror = () =>
        reject(allReq.error ?? new Error('Database error'));
    };
    req.onerror = () => reject(req.error ?? new Error('Database error'));
  });
}

// ---------------------------------------------------------------------------
// Timeline cache — stores enriched timeline entries to avoid re-fetching
// ---------------------------------------------------------------------------

interface TimelineCacheRecord {
  key: string;
  entries: unknown[];
  timestamp: number;
  refreshBoundary?: number;
}

/** Max age for timeline cache (10 minutes) */
const TIMELINE_CACHE_MAX_AGE_MS = 10 * 60 * 1000;

export async function dbGetTimelineCache(cacheKey: string): Promise<{
  entries: unknown[];
  timestamp: number;
  refreshBoundary: number;
} | null> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_TIMELINE, 'readonly');
      const req = tx.objectStore(STORE_TIMELINE).get(cacheKey);
      req.onsuccess = () => {
        const record = req.result as TimelineCacheRecord | undefined;
        if (!record) {
          resolve(null);
          return;
        }
        if (Date.now() - record.timestamp > TIMELINE_CACHE_MAX_AGE_MS) {
          resolve(null);
          return;
        }
        resolve({
          entries: record.entries,
          timestamp: record.timestamp,
          refreshBoundary: record.refreshBoundary ?? record.timestamp,
        });
      };
      req.onerror = () => reject(req.error ?? new Error('Database error'));
    });
  } catch {
    return null;
  }
}

export async function dbSaveTimelineCache(
  cacheKey: string,
  entries: unknown[],
  refreshBoundary = Date.now(),
): Promise<void> {
  try {
    const db = await openDb();
    return new Promise((resolve, reject) => {
      const tx = db.transaction(STORE_TIMELINE, 'readwrite');
      tx.objectStore(STORE_TIMELINE).put({
        key: cacheKey,
        entries,
        timestamp: Date.now(),
        refreshBoundary,
      } satisfies TimelineCacheRecord);
      tx.oncomplete = () => resolve();
      tx.onerror = () => reject(tx.error ?? new Error('Database error'));
    });
  } catch {
    // Cache write failure is non-fatal
  }
}
